import { z } from 'zod';
import { isRealDate, paginationQuerySchema, uuidSchema } from './common.js';
const text = (max) => z
    .string()
    .trim()
    .max(max)
    .refine((v) => !v.includes('\u0000'), 'Invalid character');
const optionalText = (max) => text(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine(isRealDate, 'Invalid calendar date');
const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM');
const atLeastOne = (v) => Object.keys(v).length > 0;
/** A number with at most two decimals (marks and percentages are stored as numeric(_,2)). */
const twoDecimals = z
    .number()
    .finite()
    .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6, 'At most 2 decimal places');
// ------------------------------------------------------------ grading scales
export const gradingBandSchema = z.object({
    grade: text(10).min(1),
    minPercent: twoDecimals.refine((v) => v >= 0 && v <= 100, 'Between 0 and 100'),
    gradePoint: twoDecimals
        .refine((v) => v >= 0 && v <= 99, 'Between 0 and 99')
        .nullable()
        .optional(),
    remark: optionalText(100),
    isPass: z.boolean().default(true),
});
const bandsSchema = z
    .array(gradingBandSchema)
    .min(1)
    .max(20)
    .superRefine((bands, ctx) => {
    if (!bands.some((b) => b.minPercent === 0))
        ctx.addIssue({ code: 'custom', message: 'A band must start at 0%' });
    if (new Set(bands.map((b) => b.minPercent)).size !== bands.length)
        ctx.addIssue({ code: 'custom', message: 'Two bands start at the same percentage' });
    if (new Set(bands.map((b) => b.grade.toLowerCase())).size !== bands.length)
        ctx.addIssue({ code: 'custom', message: 'Grade names must be unique' });
});
export const createGradingScaleSchema = z.object({ name: text(100).min(1), bands: bandsSchema });
export const updateGradingScaleSchema = z
    .object({ name: text(100).min(1), bands: bandsSchema })
    .partial()
    .refine(atLeastOne, 'At least one field is required');
// -------------------------------------------------------------------- exams
export const EXAM_TYPES = ['unit_test', 'term', 'final', 'practical', 'other'];
export const EXAM_STATUSES = ['draft', 'scheduled', 'marking', 'verified', 'published'];
export const createExamSchema = z
    .object({
    academicYearId: uuidSchema.optional(),
    termId: uuidSchema.nullable().optional(),
    gradingScaleId: uuidSchema,
    name: text(100).min(1),
    examType: z.enum(EXAM_TYPES).default('term'),
    startDate: isoDate,
    endDate: isoDate,
})
    .refine((v) => v.endDate >= v.startDate, {
    message: 'End date cannot be before start date',
    path: ['endDate'],
});
export const updateExamSchema = z
    .object({
    termId: uuidSchema.nullable(),
    gradingScaleId: uuidSchema,
    name: text(100).min(1),
    examType: z.enum(EXAM_TYPES),
    startDate: isoDate,
    endDate: isoDate,
})
    .partial()
    .refine(atLeastOne, 'At least one field is required')
    .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    message: 'End date cannot be before start date',
    path: ['endDate'],
});
export const listExamsQuerySchema = z.object({
    academicYearId: uuidSchema.optional(),
    status: z.enum(EXAM_STATUSES).optional(),
});
export const transitionExamSchema = z.object({
    to: z.enum(EXAM_STATUSES),
    reason: optionalText(300),
});
// ------------------------------------------------------------------- papers
const paperFields = {
    examDate: isoDate,
    startTime: clockTime.nullable(),
    endTime: clockTime.nullable(),
    maxMarks: twoDecimals.refine((v) => v > 0 && v <= 1000, 'Between 0 and 1000'),
    passMarks: twoDecimals.refine((v) => v >= 0 && v <= 1000, 'Between 0 and 1000'),
};
export const createExamPaperSchema = z
    .object({
    classId: uuidSchema,
    subjectId: uuidSchema,
    ...paperFields,
    startTime: paperFields.startTime.optional(),
    endTime: paperFields.endTime.optional(),
})
    .refine((v) => v.passMarks <= v.maxMarks, {
    message: 'Pass marks cannot exceed maximum marks',
    path: ['passMarks'],
})
    .refine((v) => !v.startTime === !v.endTime, {
    message: 'Give both start and end time, or neither',
    path: ['endTime'],
})
    .refine((v) => !v.startTime || !v.endTime || v.endTime > v.startTime, {
    message: 'End time must be after start time',
    path: ['endTime'],
});
export const updateExamPaperSchema = z
    .object(paperFields)
    .partial()
    .refine(atLeastOne, 'At least one field is required')
    .refine((v) => v.passMarks === undefined || v.maxMarks === undefined || v.passMarks <= v.maxMarks, {
    message: 'Pass marks cannot exceed maximum marks',
    path: ['passMarks'],
});
// -------------------------------------------------------------------- marks
export const MARK_STATUSES = ['present', 'absent', 'exempt'];
export const markRecordSchema = z
    .object({
    studentId: uuidSchema,
    status: z.enum(MARK_STATUSES),
    marks: twoDecimals.nullable().optional(),
    remarks: optionalText(300),
})
    .superRefine((v, ctx) => {
    if (v.status === 'present' && (v.marks === null || v.marks === undefined))
        ctx.addIssue({
            code: 'custom',
            path: ['marks'],
            message: 'Marks are required for a present student',
        });
    if (v.status !== 'present' && v.marks !== null && v.marks !== undefined)
        ctx.addIssue({
            code: 'custom',
            path: ['marks'],
            message: 'Absent or exempt students have no marks',
        });
    if (v.marks !== null && v.marks !== undefined && v.marks < 0)
        ctx.addIssue({ code: 'custom', path: ['marks'], message: 'Marks cannot be negative' });
});
export const enterMarksSchema = z
    .object({ records: z.array(markRecordSchema).min(1).max(300) })
    .refine((v) => new Set(v.records.map((r) => r.studentId)).size === v.records.length, {
    message: 'A student can appear only once',
    path: ['records'],
});
export const marksSheetQuerySchema = z.object({ sectionId: uuidSchema.optional() });
// ------------------------------------------------------------------ results
export const listResultsQuerySchema = paginationQuerySchema.extend({
    classId: uuidSchema.optional(),
    sectionId: uuidSchema.optional(),
});
//# sourceMappingURL=exams.js.map